import dayjs from 'dayjs';
import { useCallback, useEffect, useRef } from 'react';
import { SupplierInboundItem } from 'src/reducers/supplierInboundReducer';

// 세션 스토리지 키 생성 함수
const getStorageKey = (prefix: string, id: string, date: dayjs.Dayjs) => `${prefix}_${id}_${date.format('YYYY-MM-DD')}`;

interface StoredData<T> {
  data: T;
  timestamp: string;
}

// 마지막 정리 시간을 저장하는 변수 (전역에서 공유)
let lastCleanupTime = 0;
// 정리 간격 (밀리초) - 1시간마다 정리
const CLEANUP_INTERVAL = 60 * 60 * 1000;

/**
 * 세션 스토리지의 모든 항목을 검사하여 만료된 데이터를 삭제합니다.
 * @param prefix 스토리지 키 접두사
 * @param expireHours 만료 시간(시간 단위)
 * @param force 간격에 상관없이 강제로 정리 수행
 */
const cleanupExpiredItems = (prefix: string, expireHours: number, force = false): void => {
  const now = Date.now();

  // 마지막 정리 이후 일정 시간이 지나지 않았고, 강제 정리가 아니면 스킵
  if (!force && now - lastCleanupTime < CLEANUP_INTERVAL) {
    return;
  }

  try {
    const currentTime = new Date();
    const keysToRemove: string[] = [];

    // 먼저 삭제할 키를 수집
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);

      if (key && key.startsWith(`${prefix}_`)) {
        const item = sessionStorage.getItem(key);

        if (item) {
          try {
            const parsedData = JSON.parse(item) as StoredData<unknown>;
            const savedTime = new Date(parsedData.timestamp);
            const hoursDiff = (currentTime.getTime() - savedTime.getTime()) / (1000 * 60 * 60);

            if (hoursDiff > expireHours) {
              keysToRemove.push(key);
            }
          } catch (e) {
            // 파싱 실패한 항목은 삭제 목록에 추가
            keysToRemove.push(key);
          }
        }
      }
    }

    // 수집된 키를 일괄 삭제
    if (keysToRemove.length > 0) {
      keysToRemove.forEach((key) => {
        sessionStorage.removeItem(key);
      });

      if (process.env.NODE_ENV !== 'production') {
        console.log(`만료된 데이터 ${keysToRemove.length}개 삭제됨`);
      }
    }

    // 마지막 정리 시간 업데이트
    lastCleanupTime = now;
  } catch (e) {
    console.error('세션 스토리지 정리 중 오류 발생:', e);
  }
};

/**
 * 세션 스토리지에 데이터를 저장하고 불러오는 훅
 * @param prefix 스토리지 키 접두사
 * @param id 고유 식별자
 * @param date 날짜
 * @param data 저장할 데이터
 * @param setData 데이터 설정 콜백 함수
 * @param expireHours 만료 시간(시간 단위, 기본값: 24시간)
 */
export const useSessionStorage = <T>(
  prefix: string,
  id: string | undefined,
  date: dayjs.Dayjs | null,
  data: T,
  setData: (data: T) => void,
  expireHours = 24
) => {
  // 이전 ID와 날짜를 저장하는 ref
  const prevStateRef = useRef<{ id?: string; dateStr?: string }>({});

  // 세션 스토리지에서 데이터 불러오기
  const loadFromStorage = useCallback(() => {
    if (!id || !date) return false;

    const storageKey = getStorageKey(prefix, id, date);
    const storedData = sessionStorage.getItem(storageKey);

    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData) as StoredData<T>;

        // 저장 시간 확인 (설정된 시간 이내인지)
        const savedTime = new Date(parsedData.timestamp);
        const currentTime = new Date();
        const hoursDiff = (currentTime.getTime() - savedTime.getTime()) / (1000 * 60 * 60);

        // 설정된 시간 이내인 경우에만 데이터 사용
        if (hoursDiff <= expireHours) {
          setData(parsedData.data);
          return true;
        } else {
          // 만료된 데이터는 삭제
          sessionStorage.removeItem(storageKey);
          return false;
        }
      } catch (error) {
        console.error('세션 스토리지 데이터 파싱 오류:', error);
        return false;
      }
    }
    return false;
  }, [id, date, prefix, setData, expireHours]);

  // 데이터 저장
  const saveToStorage = useCallback(() => {
    if (!id || !date || !data || (Array.isArray(data) && data.length === 0)) return;

    const storageKey = getStorageKey(prefix, id, date);
    const dataToStore: StoredData<T> = {
      data,
      timestamp: new Date().toISOString(),
    };

    sessionStorage.setItem(storageKey, JSON.stringify(dataToStore));
  }, [id, date, data, prefix]);

  // 데이터 삭제
  const removeFromStorage = useCallback(() => {
    if (!id || !date) return;

    const storageKey = getStorageKey(prefix, id, date);
    sessionStorage.removeItem(storageKey);
  }, [id, date, prefix]);

  // 만료된 모든 데이터 정리
  const cleanupStorage = useCallback(
    (force = false) => {
      cleanupExpiredItems(prefix, expireHours, force);
    },
    [prefix, expireHours]
  );

  // ID 또는 날짜가 변경되었는지 확인
  useEffect(() => {
    const currentDateStr = date?.format('YYYY-MM-DD');

    // ID 또는 날짜가 변경된 경우
    if (id !== prevStateRef.current.id || currentDateStr !== prevStateRef.current.dateStr) {
      // 이전 데이터가 존재했다면 저장
      if (prevStateRef.current.id && prevStateRef.current.dateStr && Array.isArray(data) && data.length > 0) {
        const prevStorageKey = `${prefix}_${prevStateRef.current.id}_${prevStateRef.current.dateStr}`;
        const dataToStore: StoredData<T> = {
          data,
          timestamp: new Date().toISOString(),
        };
        sessionStorage.setItem(prevStorageKey, JSON.stringify(dataToStore));
      }

      // 현재 상태 업데이트
      prevStateRef.current = { id, dateStr: currentDateStr };
    }
  }, [id, date, data, prefix]);

  // 컴포넌트 마운트 시 데이터 불러오기 및 만료 데이터 정리
  useEffect(() => {
    // 만료된 데이터 정리 (정기적으로 수행)
    cleanupStorage();
    // 현재 데이터 불러오기
    loadFromStorage();
  }, [id, date, loadFromStorage, cleanupStorage]);

  // 데이터 변경 시 저장
  useEffect(() => {
    saveToStorage();
  }, [data, saveToStorage]);

  return {
    loadFromStorage,
    saveToStorage,
    removeFromStorage,
    cleanupStorage: () => cleanupStorage(true), // 강제 정리 수행
  };
};

/**
 * 거래처 입고 데이터를 세션 스토리지에서 관리하는 커스텀 훅
 */
export const useSupplierInboundStorage = (
  productSupplierId: string | undefined,
  date: dayjs.Dayjs | null,
  items: SupplierInboundItem[],
  setItems: (items: SupplierInboundItem[]) => void
) => {
  return useSessionStorage<SupplierInboundItem[]>('supplierInbound', productSupplierId, date, items, setItems);
};

export default useSupplierInboundStorage;
